import "./index.css";
import { CircleCheckIcon, ExternalLinkIcon } from "lucide-react";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { InspectorsRepository } from "@/api/inspectors/repository";
import { InspectorsService } from "@/api/inspectors/service";
import Alert from "@/components/Alert";
import {
  FileInputControl,
  InputControl,
  InputNumberControl,
  SelectControl,
} from "@/controls";
import { W9EntityType } from "@/types/users-inspectors";
import type { ISelectOption } from "@/ui-kit";
import { Row, Text } from "@/ui-kit";

type IProps = {
  setDisabled: Dispatch<SetStateAction<boolean>>;
};

export const TaxInformationForm = ({ setDisabled }: IProps) => {
  const { t } = useTranslation();
  const { setValue, setError } = useFormContext();

  const [validating, setValidating] = useState(false);
  const [isValid, setIsValid] = useState(false);

  const entityTypeOptions: ISelectOption[] = [
    { text: t("label.individual"), id: W9EntityType.Individual },
    { text: t("label.corporate"), id: W9EntityType.Corporate },
    { text: t("label.llc"), id: W9EntityType.LLC },
  ];

  const w9EntityType = useWatch({ name: "w9EntityType" });
  const w9TIN = useWatch({ name: "w9TIN" });
  const w9FileUrl: string | undefined = useWatch({ name: "w9FileUrl" });

  useEffect(() => {
    if (w9EntityType === W9EntityType.Corporate && w9TIN) {
      setValue("w9SSN", w9TIN);
    }
  }, [w9EntityType, w9TIN, setValue]);

  const handleUpload = useCallback(
    (file: File) => {
      setValidating(true);
      setIsValid(false);
      setDisabled(true);
      setError("w9File", { message: undefined });

      const repository = new InspectorsRepository(new InspectorsService());
      repository
        .validateW9Details({ side: "front", file })
        .then((response) => {
          if (response.valid) {
            const data = response.extractedData;
            const setOpts = { shouldDirty: true, shouldValidate: true };
            if (data.name) setValue("w9Name", data.name, setOpts);
            if (data.tin) setValue("w9TIN", data.tin, setOpts);
            if (data.ssn) setValue("w9SSN", data.ssn, setOpts);
            if (data.entityType)
              setValue("w9EntityType", data.entityType, setOpts);
            if (response.imageUrl)
              setValue("w9FileUrl", response.imageUrl, setOpts);

            setIsValid(true);
            setDisabled(false);
          } else {
            setError("w9File", { message: t("label.w9.invalid") });
          }
        })
        .finally(() => {
          setValidating(false);
        });
    },
    [setError, setValue, setDisabled, t],
  );

  const showFields = !!w9FileUrl;

  return (
    <>
      <a
        href="https://www.irs.gov/pub/irs-pdf/fw9.pdf"
        target="_blank"
        className="w9-banner nuco-color-brand"
        rel="noreferrer"
      >
        <Row gap="xs" alignmentVertical="center">
          <Text tone="default">{t("label.w9.form")}</Text>
          <ExternalLinkIcon size="1rem" />
        </Row>
        <Text tone="muted">{t("label.w9.banner")}</Text>
      </a>

      <FileInputControl
        label={t("label.w9.upload")}
        description={t("label.w9.description")}
        name="w9File"
        loading={validating}
        onUpload={handleUpload}
      />

      {isValid && (
        <Alert
          icon={<CircleCheckIcon size="1.25rem" />}
          title={t("label.w9.validatedTitle")}
          content={<Text tone="muted">{t("label.w9.validatedText")}</Text>}
        />
      )}

      {showFields && (
        <>
          <SelectControl
            label={t("label.entity_type")}
            name="w9EntityType"
            options={entityTypeOptions}
          />

          <InputControl label={t("label.business_name")} name="w9Name" />

          {w9EntityType === W9EntityType.Corporate ? (
            <InputNumberControl
              label={t("label.tin_ein")}
              name="w9TIN"
              maxLength={9}
              allowChars=""
              thousandsSeparator=""
            />
          ) : (
            <>
              <InputNumberControl
                label={t("label.tin")}
                name="w9TIN"
                maxLength={9}
                allowChars=""
                thousandsSeparator=""
                required={w9EntityType !== W9EntityType.Individual}
              />
              <InputNumberControl
                label={t("label.ssn_ein")}
                name="w9SSN"
                maxLength={9}
                allowChars=""
                thousandsSeparator=""
              />
            </>
          )}
        </>
      )}
    </>
  );
};
